import { TypedUseSelectorHook, useSelector, useStore } from 'react-redux';
import { dayjs, getStore, track } from './smi-bridge';
import { useCallback } from 'react';
import { AnyAction } from 'redux';
import { useStepContext } from './contexts/stepContext';
import { useCancelFlowId } from './contexts/cancelFlowIdContext';
import { formatCurrency, formatDiscountDisplay, DiscountType } from './utils/formatDiscount';

type Store = ReturnType<typeof getStore>;
export type AppState = ReturnType<Store['getState']>;

// re-export redux's hooks so that it automatically infers the correct state type
export const useAppSelector: TypedUseSelectorHook<AppState> = useSelector;
export const useAppStore = useStore<AppState, AnyAction>;

function formatWithDayjs(locale: string, value: string, format: string) {
  return dayjs(value).locale(locale).format(format);
}

export const useLocaleDateFormat = () => {
  const locale = useAppSelector(state => state.locale);

  return useCallback(
    (value: string, format = 'LL') => {
      const languageCode = locale.substring(0, 2);
      try {
        // may throw if locale not loaded
        return formatWithDayjs(languageCode, value, format);
      } catch {
        return formatWithDayjs('en', value, format);
      }
    },
    [locale]
  );
};

export const useLocaleCurrencyFormat = () => {
  const locale = useAppSelector(state => state.locale);

  return useCallback(
    (price: string, currencyCode: string) => {
      const numericPrice = parseFloat(price);
      if (isNaN(numericPrice)) return price;
      return formatCurrency(numericPrice, currencyCode, locale);
    },
    [locale]
  );
};

/**
 * Hook that returns a function to format discount values with the user's locale.
 */
export const useLocaleDiscountFormat = () => {
  const locale = useAppSelector(state => state.locale);

  return useCallback(
    (value: string | number, discountType: DiscountType, currencyCode: string = 'USD') => {
      return formatDiscountDisplay(value, discountType, currencyCode, locale);
    },
    [locale]
  );
};

/**
 * Hook that provides a track function for SM analytics. It automatically includes the cancelFlowId and the cancelReason as event attributes, if set in context.
 */
export const useTrack = () => {
  const { flowContext } = useStepContext();
  const cancelFlowId = useCancelFlowId();

  return useCallback<typeof track>(
    (event, attributes) => {
      const enhancedAttributes = {
        ...attributes,
        ...(cancelFlowId && { cancelFlowId }),
        ...(flowContext.cancelReason && { cancelReason: flowContext.cancelReason })
      };

      return track(event, enhancedAttributes);
    },
    [cancelFlowId, flowContext.cancelReason]
  );
};
